import { useState } from 'react';
import { flushSync } from 'react-dom';
import { Helmet } from 'react-helmet';
import { v4 } from 'uuid';

import { SingSetup, SongPreview } from '~/interfaces';
import CameraManager from '~/modules/camera/camera-manager';
import NoPrerender from '~/modules/elements/no-prerender';
import useFullscreen from '~/modules/hooks/use-fullscreen';
import useQueryParam from '~/modules/hooks/use-query-param';
import PlayersManager from '~/modules/players/players-manager';
import SongQueue from '~/modules/song-queue/song-queue';
import { fitPartsToSong, singersForRound } from '~/modules/songs/utils/fit-parts-to-song';
import { woosh } from '~/modules/sound-manager';
import startViewTransition from '~/modules/utils/start-view-transition';
import { MobilePhoneModeSetting } from '~/routes/settings/settings-state';
import SingASong from '~/routes/sing-a-song/sing-a-song';

import Singing from './singing/singing';

function Game() {
  const songId = useQueryParam('song');

  const [singSetup, setSingSetup] = useState<(SingSetup & { song: SongPreview }) | null>(null);
  const [preselectedSong, setPreselectedSong] = useState<string | null>(songId ?? null);
  const [resetKey, setResetKey] = useState(0);
  // `carriedOver` when the players come from the song before rather than from this song's card - a
  // queued song starting on its own.
  const handleSelect = (unfilteredSetup: SingSetup & { song: SongPreview }, carriedOver = false) => {
    // The last thing before a round starts, and the only seam every way into one goes through -
    // Play, Practice, and the queue popping the next song below. Everything that scores, ranks and
    // records reads the live roster rather than this setup, so the roster is what has to be right;
    // the setup is then made to match so the two cannot disagree about who is singing.
    //
    // A queued song carries the roster it was queued against. Players who left since are dropped,
    // and phones seated since are added - without that they had no PlayerState and scored nothing.
    const singing = PlayersManager.dropUnassignedPlayers();
    const { players, changed } = singersForRound(unfilteredSetup.players, singing);
    const setup = {
      ...unfilteredSetup,
      players: fitPartsToSong(players, unfilteredSetup.song.tracksCount, {
        carriedOver: carriedOver || changed,
        partsOffered: !MobilePhoneModeSetting.get(),
      }),
    };

    const previewVideo = document.getElementById('preview-video-container');

    if (previewVideo) {
      previewVideo.style.viewTransitionName = 'song-preview-video';
      startViewTransition(() => {
        previewVideo.style.viewTransitionName = '';
        flushSync(() => {
          setSingSetup(setup);
        });
      });
    } else {
      setSingSetup(setup);
    }

    woosh.play(false);
  };

  useFullscreen();

  return (
    <>
      <Helmet>
        <title>Game | AllKaraoke.Party - Free Online Karaoke Party Game</title>
      </Helmet>
      <NoPrerender>
        {singSetup ? (
          <Singing
            restartSong={() => {
              CameraManager.restartRecord();
              setResetKey((current) => current + 1);
            }}
            key={resetKey}
            songPreview={singSetup.song}
            singSetup={singSetup}
            returnToSongSelection={() => {
              // The party's running order takes over when a song finishes. With an empty queue
              // this is exactly the old behaviour - back to the song list.
              const next = SongQueue.popNext();
              if (next) {
                handleSelect(
                  {
                    id: v4(),
                    song: next.song,
                    mode: next.override?.mode ?? singSetup.mode,
                    tolerance: next.override?.tolerance ?? singSetup.tolerance,
                    players: next.override?.players ?? singSetup.players,
                  },
                  !next.override,
                );
                setResetKey((current) => current + 1);
                return;
              }

              setPreselectedSong(singSetup.song.id);
              setSingSetup(null);
            }}
          />
        ) : (
          <SingASong onSongSelected={(setup) => handleSelect(setup)} preselectedSong={preselectedSong} />
        )}
      </NoPrerender>
    </>
  );
}
export default Game;
